import { inject, Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { doSupabaseCall } from '../../shared/helpers/supabase.helper';
import type { IGenerationState } from '../../shared/models';
import { AuthService } from '../auth/auth.service';
import { SupabaseService } from '../supabase/supabase';

// Stato di generazione (interview_generation_app, migrazione 0030): ogni output di Claude
// viene salvato appena prodotto — refresh-proof, mai ripagare una chiamata AI già fatta.
// Una riga per intervista, upsert: le colonne non passate restano invariate.
@Injectable({ providedIn: 'root' })
export class GenerationService {
  // --- private ---
  private readonly _supabase: SupabaseService = inject(SupabaseService);
  private readonly _auth: AuthService = inject(AuthService);

  // --- public ---
  public save(
    interviewId: string,
    fields: Partial<Pick<IGenerationState, 'fase_a_md' | 'cli_md' | 'arch_md'>>,
  ): Observable<void> {
    const client = this._supabase.client;
    const session = this._auth.sessionSignal();
    if (null === client || null === session) {
      return of(undefined);
    }
    return doSupabaseCall(
      client.from('interview_generation_app').upsert(
        {
          fk_interview_app_id: interviewId,
          fk_auth_users_id: session.user.id,
          date_upd: new Date().toISOString(),
          ...fields,
        },
        { onConflict: 'fk_interview_app_id' },
      ),
    ).pipe(map(() => undefined));
  }

  public get(interviewId: string): Observable<IGenerationState | null> {
    const client = this._supabase.client;
    if (null === client) {
      return of(null);
    }
    return doSupabaseCall<IGenerationState | null>(
      client
        .from('interview_generation_app')
        .select('fase_a_md, cli_md, arch_md, job_status, job_error')
        .eq('fk_interview_app_id', interviewId)
        .maybeSingle(),
    ).pipe(map((row) => row ?? null));
  }
}
